import { byId } from "@/js/common";
import { sortLinkedList } from "@/js/linkedList";
import { useUser } from "@/js/providers/UserProvider";
import { Plan, usePlans } from "@/js/resources";
import { requireNotNull } from "@enymo/ts-nullsafe";
import axios, { AxiosError, CanceledError } from "axios";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { route } from "ziggy-js";
import SelectPlanComponent from "../components/pages/SelectPlan";
import useAbortEffect from "../hooks/AbortEffectHook";
import useLocalStorage from "../hooks/LocalStorageHook";

export default function SelectPlan() {
    const {i18n} = useTranslation();
    const {user} = useUser();
    const [plans] = usePlans();
    const [searchParams, setSearchParams] = useSearchParams();
    const plan = searchParams.get("plan");
    const [privatePlanId, setPrivatePlanId] = useLocalStorage<string | undefined>('selected_plan');
    const [privatePlan, setPrivatePlan] = useState<Plan | null | undefined>(privatePlanId ? null : undefined);
    const overrideSession = searchParams.get("overrideSelection") === "true";
    const navigate = useNavigate();

    const sortedPlans = useMemo(() => sortLinkedList(plans).filter(({id, period}) => id !== user?.plan?.id && (user?.plan?.period !== "yearly" || period === "yearly")), [plans]);

    useAbortEffect(signal => {
        if (privatePlanId !== undefined) {
            axios.get<{data: Plan}>(route("plans.show", {plan: privatePlanId}), {signal})
                .then(response => setPrivatePlan(response.data.data))
                .catch(e => {
                    if (e instanceof AxiosError && e.response?.status === 404) {
                        setPrivatePlanId(undefined);
                        setSearchParams({});
                    }
                    else if (!(e instanceof CanceledError)) throw e;
                })
        }
    }, [privatePlanId, setPrivatePlan, setSearchParams]);
    
    const handleSelectPlan = (id: number) => {
        const plan = requireNotNull(plans.find(byId(id))!, "plan not found");
        if (user === null) {
            setPrivatePlanId(plan.sid);
            const searchParams = new URLSearchParams;
            searchParams.append("redirect", "/select-plan");
            navigate(`/sign-up?${searchParams.toString()}`)
        }
        else {
            window.location.href = route("subscriptions.join", {sid: plan.sid, locale: i18n.language});
        }
    }

    if (plan !== null && plan !== privatePlanId) {
        setPrivatePlanId(plan);
        return null;
    }

    return (
        <SelectPlanComponent
            className="py-24"
            loggedIn={user !== null}
            privateOffer={!overrideSession ? privatePlan ? {
                ...privatePlan,
                trial_days: privatePlan.trial_days ?? undefined,
                trial_price: privatePlan.trial_price ?? undefined
            } : privatePlan : undefined}
            offers={sortedPlans.map(({sid, trial_days, trial_price, ...props}) => ({
                ...props,
                trial_days: trial_days ?? undefined,
                trial_price: trial_price ?? undefined
            }))}
            onClickOffer={handleSelectPlan}
            onRejectPrivateOffer={() => setSearchParams({overrideSelection: "true"})}
        />
    )
}